/**
 * Describes the fixed projections implemented by crmSyncWorker, not the full
 * capabilities of a provider's API. Changes require adapter parity tests.
 * This contract is informational: it never changes dispatch or adds mappings.
 */
const labels = {
  email: 'Email', first_name: 'First name', last_name: 'Last name',
  phone: 'Phone', company: 'Company', city: 'City', state: 'State',
};
const capability = (record_type, providerFields) => Object.freeze({
  record_type,
  fields: Object.freeze(Object.entries(providerFields).map(([lead_field, provider_field]) => Object.freeze({
    lead_field, provider_field, label: labels[lead_field],
    role: lead_field === 'email' ? 'identity' : 'property',
  }))),
  custom_mapping: false,
  notes_scope: 'full_lead_notes',
});

export const CRM_DELIVERY_CAPABILITIES = Object.freeze({
  hubspot: capability('Contact', {
    email: 'email', first_name: 'firstname', last_name: 'lastname', phone: 'phone',
    company: 'company', city: 'city', state: 'state',
  }),
  salesforce: capability('Lead', {
    email: 'Email', first_name: 'FirstName', last_name: 'LastName', phone: 'Phone',
    company: 'Company', city: 'City', state: 'State',
  }),
  zoho: capability('Lead', {
    email: 'Email', first_name: 'First_Name', last_name: 'Last_Name', phone: 'Phone',
    company: 'Company', city: 'City', state: 'State',
  }),
  gohighlevel: capability('Contact', {
    email: 'email', first_name: 'firstName', last_name: 'lastName', phone: 'phone',
    company: 'companyName', city: 'city', state: 'state',
  }),
});

export const CRM_DELIVERY_CONTRACT = Object.freeze({
  version: 1,
  scope: 'workspace',
  requires_email: true,
  custom_mapping_supported: false,
  notes_scope: 'full_lead_notes',
  repeat_delivery: 'lead_level_once_per_provider_and_kind',
  delivery_status: 'not_checked',
});

const text = value => typeof value === 'string' || typeof value === 'number'
  ? String(value).trim().slice(0, 200) || null : null;
function host(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password ? url.hostname : null;
  } catch { return null; }
}

/** Allowlist account identity; never return raw external_account/config blobs. */
function accountIdentity(provider, value) {
  const account = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  if (provider === 'hubspot') {
    const id = text(account.hub_id);
    return { id, label: text(account.hub_domain) || (id ? `Portal ${id}` : null), identity_verified: !!id };
  }
  if (provider === 'gohighlevel') {
    const id = text(account.location_id);
    return { id, label: text(account.location_name) || id, identity_verified: !!id };
  }
  // The current Salesforce and Zoho connection flows do not persist an org ID.
  // Host/region can help recognition but must not masquerade as a verified org.
  if (provider === 'salesforce') return { id: null, label: host(account.instance_url), identity_verified: false };
  if (provider === 'zoho') return { id: null, label: host(account.api_domain) || text(account.location), identity_verified: false };
  return { id: null, label: null, identity_verified: false };
}

/** Stored prerequisites only; neither token validity nor delivery is checked. */
export function crmDeliveryConnection(row, { configured = false, paused = false } = {}) {
  const provider = row?.provider;
  const capabilities = Object.hasOwn(CRM_DELIVERY_CAPABILITIES, provider) ? CRM_DELIVERY_CAPABILITIES[provider] : null;
  const status = text(row?.connect_status) || 'UNKNOWN';
  const has_credentials = typeof row?.has_credentials === 'boolean' ? row.has_credentials : null;
  let readiness = 'unknown';
  if (status === 'NEEDS_REAUTH') readiness = 'needs_reauth';
  else if (status === 'ERROR' || status === 'DISABLED') readiness = 'unavailable';
  else if (status === 'ACTIVE') {
    if (!capabilities || configured !== true) readiness = 'unavailable';
    else if (has_credentials === false) readiness = 'missing_credentials';
    else if (has_credentials === true) readiness = paused ? 'paused' : 'available';
  }
  return {
    guid: text(row?.guid), provider, connect_status: status,
    has_credentials, provider_configured: configured === true, readiness,
    account: accountIdentity(provider, row?.external_account),
    qualified_sync: row?.provider_config?.qualified_sync !== false,
    capabilities,
  };
}
